import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { User, Mail, MapPin, Briefcase, Calendar, Camera, Lock, Loader2, Building, Clock, FileText } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProfilePage = () => {
  const { user, isAuthenticated, login, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef(null);
  
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    profilePhoto: '',
    businessInfo: {
      businessName: '',
      description: '',
      serviceCategories: [],
      yearsInBusiness: '',
      serviceArea: '',
      licenseInfo: '',
      businessHours: ''
    }
  });
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    // Wait for auth context to load
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    
    // Fetch user profile data
    fetchUserProfile();
  }, [authLoading, isAuthenticated, navigate, user?.id]);

  const fetchUserProfile = async () => {
    if (!user?.id) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/users/${user.id}`);
      const userData = response.data;
      
      setFormData({
        name: userData.name || '',
        address: userData.address || '',
        profilePhoto: userData.profilePhoto || '',
        businessInfo: userData.businessInfo || {
          businessName: '',
          description: '',
          serviceCategories: [],
          yearsInBusiness: '',
          serviceArea: '',
          licenseInfo: '',
          businessHours: ''
        }
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBusinessInfoChange = (e) => {
    setFormData({
      ...formData,
      businessInfo: {
        ...formData.businessInfo,
        [e.target.name]: e.target.value
      }
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File',
        description: 'Please upload an image file (JPG, PNG, WEBP)',
        variant: 'destructive'
      });
      return;
    }
    
    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Image must be less than 2MB',
        variant: 'destructive'
      });
      return;
    }
    
    setUploadingImage(true);
    
    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result;
      
      try {
        // Update profile photo immediately
        const response = await axios.put(`${API_URL}/api/users/${user.id}`, {
          profilePhoto: base64String
        });
        
        setFormData({ ...formData, profilePhoto: base64String });
        
        toast({
          title: 'Photo Updated',
          description: 'Your profile photo has been updated successfully.'
        });
      } catch (error) {
        console.error('Error uploading photo:', error);
        toast({
          title: 'Upload Failed',
          description: 'Failed to upload photo. Please try again.',
          variant: 'destructive'
        });
      } finally {
        setUploadingImage(false);
      }
    };
    
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const updateData = {
        name: formData.name,
        address: formData.address
      };
      
      // Add business info if professional
      if (user?.userType === 'professional') {
        updateData.businessInfo = formData.businessInfo;
      }
      
      const response = await axios.put(`${API_URL}/api/users/${user.id}`, updateData);
      
      // Update user in context
      login({ ...user, ...response.data.user });
      
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been updated successfully.'
      });
      setEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Update Failed',
        description: 'Failed to update profile. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'New password and confirmation do not match.',
        variant: 'destructive'
      });
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast({
        title: 'Weak Password',
        description: 'Password must be at least 6 characters long.',
        variant: 'destructive'
      });
      return;
    }
    
    setPasswordLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/users/${user.id}/change-password`, {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      
      toast({
        title: 'Password Changed',
        description: 'Your password has been changed successfully.'
      });
      
      setPasswordModalOpen(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Error changing password:', error);
      toast({
        title: 'Password Change Failed',
        description: error.response?.data?.detail || 'Failed to change password. Please check your current password.',
        variant: 'destructive'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600 mt-2">Manage your account information and settings</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Summary Card */}
          <Card className="lg:col-span-1">
            <CardContent className="p-6">
              <div className="text-center">
                <div className="relative inline-block">
                  {formData.profilePhoto ? (
                    <img
                      src={formData.profilePhoto}
                      alt="Profile"
                      className="w-32 h-32 rounded-full object-cover mx-auto mb-4 border-4 border-orange-100"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <User className="h-16 w-16 text-white" />
                    </div>
                  )}
                  
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="absolute bottom-4 right-1/2 transform translate-x-16 bg-white border-2 border-orange-500 rounded-full p-2 hover:bg-orange-50 transition-colors"
                  >
                    {uploadingImage ? (
                      <Loader2 className="h-4 w-4 text-orange-600 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4 text-orange-600" />
                    )}
                  </button>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                </div>
                
                <h2 className="text-xl font-bold text-gray-900">{user?.name}</h2>
                <p className="text-sm text-gray-500 mt-1">{user?.email}</p>
                
                <div className="mt-4 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800">
                  {user?.userType === 'homeowner' ? 'Homeowner' : 'Professional'}
                </div>
                
                {user?.userType === 'professional' && (
                  <div className="mt-2 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
                    {user?.subscriptionTier?.charAt(0).toUpperCase() + user?.subscriptionTier?.slice(1)} Plan
                  </div>
                )}
                
                <div className="mt-6 flex items-center justify-center text-sm text-gray-600">
                  <Calendar className="h-4 w-4 mr-2" />
                  Member since {new Date(user?.createdAt || Date.now()).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>
                
                <Button
                  onClick={() => setPasswordModalOpen(true)}
                  variant="outline"
                  className="w-full mt-6"
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Change Password
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Profile Details Card */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Profile Information</CardTitle>
                {!editing && (
                  <Button
                    onClick={() => setEditing(true)}
                    variant="outline"
                  >
                    Edit Profile
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Information */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="name">Full Name</Label>
                      <div className="relative mt-1">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          disabled={!editing}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="address">Address</Label>
                      <div className="relative mt-1">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          id="address"
                          name="address"
                          placeholder="123 Main St, Toronto, ON M5V 3A8"
                          value={formData.address}
                          onChange={handleChange}
                          disabled={!editing}
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Information (Professional Only) */}
                {user?.userType === 'professional' && (
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Information</h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="businessName">Business Name</Label>
                        <div className="relative mt-1">
                          <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            id="businessName"
                            name="businessName"
                            placeholder="Your Business Name"
                            value={formData.businessInfo.businessName}
                            onChange={handleBusinessInfoChange}
                            disabled={!editing}
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="description">Business Description</Label>
                        <Textarea
                          id="description"
                          name="description"
                          placeholder="Describe your business and services..."
                          value={formData.businessInfo.description}
                          onChange={handleBusinessInfoChange}
                          disabled={!editing}
                          rows={3}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="yearsInBusiness">Years in Business</Label>
                          <Input
                            id="yearsInBusiness"
                            name="yearsInBusiness"
                            type="number"
                            placeholder="5"
                            value={formData.businessInfo.yearsInBusiness}
                            onChange={handleBusinessInfoChange}
                            disabled={!editing}
                          />
                        </div>

                        <div>
                          <Label htmlFor="serviceArea">Service Area</Label>
                          <Input
                            id="serviceArea"
                            name="serviceArea"
                            placeholder="Greater Toronto Area"
                            value={formData.businessInfo.serviceArea}
                            onChange={handleBusinessInfoChange}
                            disabled={!editing}
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="businessHours">Business Hours</Label>
                        <div className="relative mt-1">
                          <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            id="businessHours"
                            name="businessHours"
                            placeholder="Mon-Fri: 9AM-5PM"
                            value={formData.businessInfo.businessHours}
                            onChange={handleBusinessInfoChange}
                            disabled={!editing}
                            className="pl-10"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="licenseInfo">License/Certification Info</Label>
                        <div className="relative mt-1">
                          <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            id="licenseInfo"
                            name="licenseInfo"
                            placeholder="License #12345"
                            value={formData.businessInfo.licenseInfo}
                            onChange={handleBusinessInfoChange}
                            disabled={!editing}
                            className="pl-10"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {editing && (
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setEditing(false);
                        fetchUserProfile();
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </Button>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Password Change Modal */}
      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="currentPassword">Current Password</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="newPassword">New Password</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                required
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPasswordModalOpen(false);
                  setPasswordData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }}
                disabled={passwordLoading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-orange-600 hover:bg-orange-700 text-white"
                disabled={passwordLoading}
              >
                {passwordLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Changing...
                  </>
                ) : (
                  'Change Password'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Footer />
      <Toaster />
    </div>
  );
};

export default ProfilePage;